import type { FC } from 'react';
import { startTransition, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SeoProductCategoryNavigationFragmentFragmentDoc } from '@packages/gql/generated/shopping/SeoProductCategoryNavigationFragmentFragmentDoc';
import { useLocalizedPageUrlDispatch } from '@packages/shared/src/providers/LocalizedPageUrlProvider/useLocalizedPageUrl';
import { usePageTransitionDispatch } from '@packages/shared/src/providers/PageTransitionProvider/usePageTransition';

/* GraphQL */ `
  fragment SeoProductCategoryNavigationFragment on SeoCategoryNavigation {
    title
    items {
      id
      name
      pathName
    }
  }
`;

export type SeoProductCategoryNavigationProps = {
  maskedNavigation: FragmentType<typeof SeoProductCategoryNavigationFragmentFragmentDoc>;
};

/**
 * Displays the given seo category navigation under the "normal" MBA category navigation.
 * */
export const SeoProductCategoryNavigation: FC<SeoProductCategoryNavigationProps> = ({
  maskedNavigation,
}) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const navigation =
    unmask<typeof SeoProductCategoryNavigationFragmentFragmentDoc>(maskedNavigation);

  const setUrl = useLocalizedPageUrlDispatch();
  const setPageLoading = usePageTransitionDispatch();

  const handleClick = (pathName: string) => {
    setUrl(pathName);
    setPageLoading(true);
    startTransition(() => {
      router.push(pathName);
      setPageLoading(false);
    });
  };

  return (
    <ul className="list-none pb-1">
      <li className="flex py-0.5 pr-2 pl-1">
        <button
          type="button"
          className="bg-color-transparent m-0 flex flex-1 border-0 p-0 text-left"
          onClick={() => setOpen(!open)}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
              setOpen(!open);
            }
          }}
        >
          <span className="text-size-body-base flex-1 whitespace-normal">{navigation.title}</span>
          {open ? (
            <span className="icon-emp-arrow-medium-down text-color-grey-dark size-4" />
          ) : (
            <span className="icon-emp-arrow-medium-right text-color-grey-dark size-4" />
          )}
        </button>
      </li>
      {open &&
        navigation.items.map((item) => (
          <li className="text-size-body-base flex px-2 py-0.5" key={item.id}>
            <button
              type="button"
              className="bg-color-transparent m-0 flex flex-1 border-0 p-0 text-left"
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                  handleClick(item.pathName);
                }
              }}
              onClick={() => handleClick(item.pathName)}
            >
              {item.name}
            </button>
          </li>
        ))}
    </ul>
  );
};
